import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Badge,
  Button,
  Banner,
  Divider,
  EmptyState,
  CircularLoader,
  Breadcrumbs,
  PlusIcon,
  LockIcon,
  ColumnsIcon,
  GroupIcon,
  SendIcon,
  ClockIcon,
  GraphIcon,
  useDocumentTitle,
} from '@forge/common';
import { usePartner } from '@/hooks/usePartner';
import { useConnections } from '@/hooks/useConnections';
import { usePartnerKeys } from '@/hooks/usePartnerKeys';
import { useJobRuns } from '@/hooks/useJobRuns';
import { useClients } from '@/hooks/useClients';
import { useApiMutation } from '@/hooks/useApiMutation';
import { connectionsApi } from '@/api/connections';
import type { PartnerConnection } from '@/api/connections';
import type { Client } from '@/api/clients';
import MockDataBanner from '@/pages/access-management/components/MockDataBanner';
import CreateConnectionDialog from './components/CreateConnectionDialog';
import ImportKeyDialog from './components/ImportKeyDialog';
import GenerateHoneywellKeyDialog from './components/GenerateHoneywellKeyDialog';
import AddClientDialog from './components/AddClientDialog';
import ProvisionSftpUserDialog from './components/ProvisionSftpUserDialog';

type DetailTab = 'overview' | 'connections' | 'clients' | 'keys' | 'jobs' | 'activity';

const statusColor: Record<string, 'green' | 'blue' | 'orange' | 'gray' | 'red'> = {
  active: 'green',
  onboarding: 'blue',
  paused: 'orange',
  retired: 'gray',
  success: 'green',
  failed: 'red',
  running: 'blue',
};

const PartnerDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<DetailTab>('overview');
  const [showCreateConnection, setShowCreateConnection] = useState(false);
  const [showImportKey, setShowImportKey] = useState(false);
  const [showGenerateKey, setShowGenerateKey] = useState(false);
  const [copiedKeyId, setCopiedKeyId] = useState<string | null>(null);
  const [showAddClient, setShowAddClient] = useState(false);
  const [provisionClient, setProvisionClient] = useState<Client | null>(null);
  // Tracks all connection ids with an in-flight "test connection" call, so
  // testing one row doesn't clear/overwrite another row's pending state.
  const [testingIds, setTestingIds] = useState<Set<string>>(new Set());

  const { partner, isLoading, error, isUsingMockData } = usePartner(id);
  useDocumentTitle(
    partner ? `${partner.displayName} — Partner Exchange — OneRing Portal` : 'Partner — OneRing Portal',
  );

  const {
    connections,
    isLoading: connectionsLoading,
    reload: reloadConnections,
  } = useConnections({ partnerId: id, enabled: activeTab === 'connections' || activeTab === 'overview' });

  const { keys, isLoading: keysLoading, reload: reloadKeys } = usePartnerKeys(id, {
    enabled: activeTab === 'keys' || activeTab === 'overview',
  });

  const { runs, isLoading: runsLoading } = useJobRuns({
    filters: { partnerId: id },
    enabled: activeTab === 'jobs' || activeTab === 'activity' || activeTab === 'overview',
  });

  const {
    clients,
    isLoading: clientsLoading,
    reload: reloadClients,
  } = useClients({ partnerId: id, enabled: activeTab === 'clients' });

  const activePartnerPublicKey = useMemo(
    () => keys.find((k) => k.role === 'partner_public' && k.isActive),
    [keys],
  );
  const activeHoneywellKey = useMemo(
    () => keys.find((k) => k.role === 'honeywell_pair' && k.isActive),
    [keys],
  );

  const testMutation = useApiMutation<string, void>({
    mutationFn: async (connectionId) => {
      setTestingIds((prev) => new Set(prev).add(connectionId));
      try {
        const result = await connectionsApi.test(connectionId);
        if (!result.ok) throw new Error(result.message ?? 'Connection test failed');
      } finally {
        setTestingIds((prev) => {
          const next = new Set(prev);
          next.delete(connectionId);
          return next;
        });
      }
    },
    successMessage: 'Connection test succeeded',
    errorMessage: 'Connection test failed',
    onSuccess: () => reloadConnections(),
  });

  const handleCopyPublicKey = async (keyId: string, armored: string) => {
    try {
      await navigator.clipboard.writeText(armored);
      setCopiedKeyId(keyId);
      setTimeout(() => setCopiedKeyId((prev) => (prev === keyId ? null : prev)), 2000);
    } catch {
      // Clipboard can be unavailable (e.g. non-secure context); fail silently.
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center p-4">
        <CircularLoader size="large" />
      </div>
    );
  }

  if (error || !partner) {
    return (
      <div className="p-4">
        <Banner
          variant="inline"
          type="error"
          title="Failed to load partner"
          description={error ?? 'Partner not found'}
          canClose={false}
        />
      </div>
    );
  }

  const navItems: Array<{ id: DetailTab; label: string; icon: JSX.Element; count?: number }> = [
    { id: 'overview', label: 'Overview', icon: <GraphIcon /> },
    { id: 'connections', label: 'Connections', icon: <ColumnsIcon />, count: connections.length },
    { id: 'clients', label: 'Clients', icon: <GroupIcon /> },
    { id: 'keys', label: 'Keys', icon: <LockIcon />, count: keys.length },
    { id: 'jobs', label: 'Jobs', icon: <SendIcon /> },
    { id: 'activity', label: 'Activity', icon: <ClockIcon /> },
  ];

  return (
    <div className="p-4">
      <Breadcrumbs
        crumbs={[
          { id: 'partners', href: '/partners', label: 'Partners' },
          { id: 'current', label: partner.displayName, noAction: true },
        ]}
        onChange={({ href }) => {
          if (href) navigate(href);
        }}
      />

      <div className="mt-3 mb-1 flex items-center gap-3">
        <h2 className="text-xl font-bold">{partner.displayName}</h2>
        <Badge color={statusColor[partner.status] ?? 'gray'}>{partner.status}</Badge>
      </div>
      <p className="text-sm text-color-tertiary mb-4 font-mono">{partner.code}</p>

      {isUsingMockData && (
        <div className="mb-4">
          <MockDataBanner featureName="Partner detail" />
        </div>
      )}

      <div className="mt-4 flex flex-col gap-6 md:flex-row">
        <div className="mt-4 shrink-0 md:w-52">
          <Card>
            <CardContent>
              <p className="mb-2 text-xs font-bold uppercase text-color-tertiary">Sections</p>
              <Divider />
              <nav className="mt-2 flex flex-col gap-1" aria-label="Partner sections">
                {navItems.map((item) => {
                  const isActive = activeTab === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setActiveTab(item.id)}
                      aria-current={isActive ? 'page' : undefined}
                      className={`flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm transition-colors ${
                        isActive
                          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      <span className="flex-shrink-0">{item.icon}</span>
                      <span className="flex-1">{item.label}</span>
                      {typeof item.count === 'number' && (
                        <span className="text-xs text-color-tertiary tabular-nums">
                          {item.count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </nav>
            </CardContent>
          </Card>
        </div>

        <div className="min-w-0 flex-1">
      {activeTab === 'overview' && (
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <Card>
            <CardHeader title="Details" />
            <CardContent>
              <dl className="flex flex-col gap-3 text-sm">
                <div>
                  <dt className="text-xs text-color-tertiary">Egress Allowlist Note</dt>
                  <dd>{partner.egressAllowlistNote ?? '—'}</dd>
                </div>
                <div>
                  <dt className="text-xs text-color-tertiary">Notes</dt>
                  <dd>{partner.notes ?? '—'}</dd>
                </div>
                <div>
                  <dt className="text-xs text-color-tertiary">Created</dt>
                  <dd>{new Date(partner.createdAt).toLocaleString()}</dd>
                </div>
              </dl>
            </CardContent>
          </Card>
          <Card>
            <CardHeader title="Keyring Status" />
            <CardContent>
              <div className="flex flex-col gap-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <LockIcon /> Partner public key
                  </span>
                  <Badge color={activePartnerPublicKey ? 'green' : 'orange'}>
                    {activePartnerPublicKey ? 'On file' : 'Missing'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <LockIcon /> Honeywell keypair
                  </span>
                  <Badge color={activeHoneywellKey ? 'green' : 'orange'}>
                    {activeHoneywellKey ? 'On file' : 'Missing'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <ColumnsIcon /> Connections
                  </span>
                  <Badge color={connections.length > 0 ? 'green' : 'orange'}>
                    {String(connections.length)}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'connections' && (
        <div className="mt-4">
          <Card>
            <CardHeader
              title="Connections"
              subtitle={`${connections.length} connection${connections.length !== 1 ? 's' : ''}`}
              slotRight={
                <Button
                  variant="primary"
                  icon={<PlusIcon />}
                  onClick={() => setShowCreateConnection(true)}
                >
                  Add Connection
                </Button>
              }
            />
            <CardContent>
              {connectionsLoading ? (
                <div className="flex min-h-[160px] items-center justify-center">
                  <CircularLoader />
                </div>
              ) : connections.length === 0 ? (
                <EmptyState icon={<ColumnsIcon />} description="No connections configured" />
              ) : (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Label</TableCell>
                      <TableCell>Direction</TableCell>
                      <TableCell>Host</TableCell>
                      <TableCell>Auth</TableCell>
                      <TableCell>Last Test</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {connections.map((conn: PartnerConnection) => (
                      <TableRow key={conn.id}>
                        <TableCell>{conn.label}</TableCell>
                        <TableCell>
                          <Badge color="blue">{conn.direction}</Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-xs font-mono">
                            {conn.host}:{conn.port}
                          </span>
                        </TableCell>
                        <TableCell>{conn.authType === 'ssh_key' ? 'SSH key' : 'Password'}</TableCell>
                        <TableCell>
                          {conn.lastTestStatus ? (
                            <Badge color={conn.lastTestStatus === 'ok' ? 'green' : 'red'}>
                              {conn.lastTestStatus}
                            </Badge>
                          ) : (
                            <span className="text-xs text-color-tertiary">Never tested</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="tertiary"
                            size="regular"
                            disabled={testingIds.has(conn.id)}
                            onClick={() => testMutation.mutate(conn.id)}
                          >
                            {testingIds.has(conn.id) ? 'Testing...' : 'Test'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'clients' && (
        <div className="mt-4">
          <Card>
            <CardHeader
              title="Clients"
              subtitle={`${clients.length} client${clients.length !== 1 ? 's' : ''}`}
              slotRight={
                <Button
                  variant="primary"
                  icon={<PlusIcon />}
                  onClick={() => setShowAddClient(true)}
                >
                  Add Client
                </Button>
              }
            />
            <CardContent>
              {clientsLoading ? (
                <div className="flex min-h-[160px] items-center justify-center">
                  <CircularLoader />
                </div>
              ) : clients.length === 0 ? (
                <EmptyState
                  icon={<GroupIcon />}
                  description="No clients yet. Add the external people at this utility who post or collect files."
                />
              ) : (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Company</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clients.map((client) => (
                      <TableRow key={client.id}>
                        <TableCell>{client.name}</TableCell>
                        <TableCell>
                          <span className="text-xs">{client.email}</span>
                        </TableCell>
                        <TableCell>{client.company ?? '—'}</TableCell>
                        <TableCell>
                          <Button
                            variant="tertiary"
                            size="regular"
                            icon={<LockIcon />}
                            onClick={() => setProvisionClient(client)}
                          >
                            Provision SFTP
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'keys' && (
        <div className="mt-4">
          <Card>
            <CardHeader
              title="PGP Keyring"
              subtitle={`${keys.length} key${keys.length !== 1 ? 's' : ''}`}
              slotRight={
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    icon={<LockIcon />}
                    onClick={() => setShowGenerateKey(true)}
                  >
                    Generate Honeywell keypair
                  </Button>
                  <Button
                    variant="primary"
                    icon={<PlusIcon />}
                    onClick={() => setShowImportKey(true)}
                  >
                    Import Key
                  </Button>
                </div>
              }
            />
            <CardContent>
              {keysLoading ? (
                <div className="flex min-h-[160px] items-center justify-center">
                  <CircularLoader />
                </div>
              ) : keys.length === 0 ? (
                <EmptyState icon={<LockIcon />} description="No keys imported yet" />
              ) : (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Role</TableCell>
                      <TableCell>Fingerprint</TableCell>
                      <TableCell>Algorithm</TableCell>
                      <TableCell>Expires</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {keys.map((key) => (
                      <TableRow key={key.id}>
                        <TableCell>
                          {key.role === 'partner_public' ? 'Partner public' : 'Honeywell keypair'}
                        </TableCell>
                        <TableCell>
                          <span className="text-xs font-mono break-all">{key.fingerprint}</span>
                        </TableCell>
                        <TableCell>
                          {key.algorithm ?? '—'}
                          {key.keySize ? ` (${key.keySize}-bit)` : ''}
                        </TableCell>
                        <TableCell>
                          {key.expiresAt ? new Date(key.expiresAt).toLocaleDateString() : '—'}
                        </TableCell>
                        <TableCell>
                          <Badge color={key.isActive ? 'green' : 'gray'}>
                            {key.revokedAt ? 'Revoked' : key.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="tertiary"
                            onClick={() => handleCopyPublicKey(key.id, key.armoredPublic)}
                          >
                            {copiedKeyId === key.id ? 'Copied' : 'Copy public key'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {(activeTab === 'jobs' || activeTab === 'activity') && (
        <div className="mt-4">
          <Card>
            <CardHeader
              title={activeTab === 'jobs' ? 'Scheduled Jobs' : 'Recent Activity'}
              subtitle={`${runs.length} run${runs.length !== 1 ? 's' : ''}`}
            />
            <CardContent>
              {runsLoading ? (
                <div className="flex min-h-[160px] items-center justify-center">
                  <CircularLoader />
                </div>
              ) : runs.length === 0 ? (
                <EmptyState icon={<SendIcon />} description="No job runs yet" />
              ) : (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Job</TableCell>
                      <TableCell>Direction</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Started</TableCell>
                      <TableCell>Files</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {runs.map((run) => (
                      <TableRow key={run.id}>
                        <TableCell>
                          <span className="font-mono text-xs">{run.jobName}</span>
                        </TableCell>
                        <TableCell>{run.direction}</TableCell>
                        <TableCell>
                          <Badge color={statusColor[run.status] ?? 'gray'}>{run.status}</Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-xs text-color-secondary">
                            {new Date(run.startedAt).toLocaleString()}
                          </span>
                        </TableCell>
                        <TableCell>{run.filesProcessed}</TableCell>
                        <TableCell>
                          {run.githubRunUrl && (
                            <a
                              className="text-xs text-blue-600 underline"
                              href={run.githubRunUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <GraphIcon /> View run
                            </a>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      )}

        </div>
      </div>

      {showCreateConnection && id && (
        <CreateConnectionDialog
          partnerId={id}
          onClose={() => setShowCreateConnection(false)}
          onSuccess={() => {
            setShowCreateConnection(false);
            reloadConnections();
          }}
        />
      )}

      {showImportKey && id && (
        <ImportKeyDialog
          partnerId={id}
          onClose={() => setShowImportKey(false)}
          onSuccess={() => {
            setShowImportKey(false);
            reloadKeys();
          }}
        />
      )}

      {showGenerateKey && id && (
        <GenerateHoneywellKeyDialog
          partnerId={id}
          partnerName={partner.displayName}
          hasExistingKey={Boolean(activeHoneywellKey)}
          onClose={() => setShowGenerateKey(false)}
          onSuccess={() => {
            setShowGenerateKey(false);
            reloadKeys();
          }}
        />
      )}

      {showAddClient && id && (
        <AddClientDialog
          partnerId={id}
          onClose={() => setShowAddClient(false)}
          onSuccess={() => {
            setShowAddClient(false);
            reloadClients();
          }}
        />
      )}

      {provisionClient && (
        <ProvisionSftpUserDialog
          client={provisionClient}
          onClose={() => setProvisionClient(null)}
          onSuccess={() => setProvisionClient(null)}
        />
      )}
    </div>
  );
};

export default PartnerDetailPage;
